import { v2, type Document } from '@/api/v2Client';
import { sectionKey } from '@/lib/sectionKey';

export interface DocumentDropPosition {
  readonly documentId: string;
  readonly edge: 'before' | 'after';
}

/** Resolve the visible insertion line against IDs, never a stale array index. */
export function insertDocumentIds(
  current: readonly string[],
  incoming: readonly string[],
  position?: DocumentDropPosition,
): string[] {
  if (position && incoming.includes(position.documentId)) return [...current];
  const remaining = current.filter((id) => !incoming.includes(id));
  const anchor = position ? remaining.indexOf(position.documentId) : remaining.length;
  if (anchor < 0) throw new Error('The target document moved. Please try the drop again.');
  remaining.splice(anchor + (position?.edge === 'after' ? 1 : 0), 0, ...incoming);
  return remaining;
}

function sectionDocuments(documents: readonly Document[], sectionCode: string): Document[] {
  return documents.filter((doc) => sectionKey(doc.CtdSectionCode ?? doc.Classification ?? '') === sectionKey(sectionCode));
}

function assertOrderEditable(documents: readonly Document[]): void {
  if (documents.some((doc) => doc.IsLocked)) {
    throw new Error('Unlock documents in this section before changing their order.');
  }
}

export async function readDropTarget(submissionId: string, sectionCode: string, position?: DocumentDropPosition): Promise<Document[]> {
  const documents: Document[] = [];
  for (let offset = 0; ; offset += 200) {
    const page = await v2.documents.listBySubmission(submissionId, { limit: 200, offset });
    documents.push(...page);
    if (page.length < 200) break;
  }
  const bucket = sectionDocuments(documents, sectionCode);
  assertOrderEditable(bucket);
  insertDocumentIds(bucket.map((doc) => doc.Id), [], position);
  return bucket;
}

export async function orderDroppedDocuments(
  submissionId: string,
  sectionCode: string,
  documentIds: readonly string[],
  position?: DocumentDropPosition,
): Promise<string[]> {
  const bucket = await readDropTarget(submissionId, sectionCode, position);
  const original = bucket.map((doc) => doc.Id);
  if (documentIds.some((id) => !original.includes(id))) {
    throw new Error('A document moved out of this section. Please try the drop again.');
  }
  const order = insertDocumentIds(original, documentIds, position);
  if (order.some((id, index) => id !== original[index])) {
    await v2.documentReorder.reorder(submissionId, order);
  }
  return original;
}

/** A batch keeps Finder's file order even when uploads take different amounts of time. */
export async function uploadDroppedFiles(
  submissionId: string,
  sectionCode: string,
  files: readonly File[],
  position: DocumentDropPosition | undefined,
  onFileError: (file: File, error: unknown) => void,
): Promise<void> {
  if (files.length === 0) return;
  await readDropTarget(submissionId, sectionCode, position);
  const uploaded: string[] = [];
  for (const file of files) {
    let pendingId: string | null = null;
    try {
      const created = await v2.documents.create({ SubmissionId: submissionId, FileName: file.name, CtdSectionCode: sectionCode });
      pendingId = created.Id;
      await v2.documents.uploadFile(created.Id, file);
      pendingId = null;
      uploaded.push(created.Id);
    } catch (error) {
      if (pendingId) await v2.documents.abandonPendingUpload(pendingId).catch(() => { /* best-effort cleanup */ });
      onFileError(file, error);
    }
  }
  if (uploaded.length === 0) return;
  try {
    await orderDroppedDocuments(submissionId, sectionCode, uploaded, position);
  } catch (error) {
    throw new Error(`Files uploaded, but their order could not be saved. ${error instanceof Error ? error.message : 'Please reorder them in the tree.'}`);
  }
}
